import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

interface Product {
  id: number;
  slug: string;
  name: string;
  price_cents: number;
  currency: string;
  billing_interval: string;
  status: string;
}

interface Tenant {
  id: number;
  slug: string;
  company_name: string;
  admin_email: string;
  status: string;
  app_version: string | null;
  activated_at: string | null;
}

interface Stats {
  tenants: Record<string, number>;
  mrr_cents: number;
  mrr_currency: string | null;
  queue_depth: number;
  jobs_24h: Record<string, number>;
  events_24h: { total: number; processed: number; errored: number; unprocessed: number };
  backups: {
    stale: number;
    failed_last_attempt: number;
    never_backed_up: number;
    healthy: number;
    tenants_with_issues: Array<{
      id: number;
      slug: string;
      status: string;
      last_success_at: string | null;
      last_status: string | null;
      issue: 'stale' | 'failed_last_attempt' | 'never_backed_up';
    }>;
  };
}

function formatMoney(cents: number, currency: string | null): string {
  if (!currency) return '—';
  return (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: currency.toUpperCase() });
}

// Status group → display label. The dashboard chips collapse some
// rarely-seen statuses under 'other' to avoid 8 noisy chips when most
// platforms have 3 active states.
const STATUS_CHIPS: Array<{ key: string; label: string; statuses: string[] }> = [
  { key: 'all',       label: 'All',       statuses: [] },
  { key: 'live',      label: 'Live',      statuses: ['active', 'trialing', 'past_due'] },
  { key: 'suspended', label: 'Suspended', statuses: ['suspended'] },
  { key: 'failed',    label: 'Failed',    statuses: ['failed'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
];

export function Dashboard() {
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushInfo, setPushInfo] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('live');

  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    slug: '', company_name: '', admin_email: '',
    product_id: '', price_cents: '', billing_interval: '', notes: '',
  });

  useEffect(() => {
    let cancelled = false;
    const reload = () => {
      api<{ tenants: Tenant[] }>('/admin/tenants')
        .then((d) => { if (!cancelled) setTenants(d.tenants); })
        .catch((e) => { if (!cancelled) setError((e as Error).message); });
      api<Stats>('/admin/stats')
        .then((s) => { if (!cancelled) setStats(s); })
        .catch(() => {});
    };
    reload();
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (!timer) timer = setInterval(reload, 30_000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    start();
    const onVis = () => (document.hidden ? stop() : (reload(), start()));
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    if (showCreate && !products) {
      api<{ products: Product[] }>('/admin/products')
        .then((d) => setProducts(d.products))
        .catch(() => {});
    }
  }, [showCreate, products]);

  function updateCreate(field: string, value: string) {
    setCreateForm((f) => ({ ...f, [field]: value }));
    if (field === 'company_name') {
      const slug = value.toLowerCase().trim()
        .replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-').slice(0, 32);
      setCreateForm((f) => ({ ...f, slug }));
    }
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreateBusy(true);
    try {
      const body: Record<string, unknown> = {
        slug: createForm.slug,
        company_name: createForm.company_name,
        admin_email: createForm.admin_email,
        product_id: Number(createForm.product_id),
      };
      if (createForm.price_cents) body.price_cents = Number(createForm.price_cents);
      if (createForm.billing_interval) body.billing_interval = createForm.billing_interval;
      if (createForm.notes) body.notes = createForm.notes;
      await api('/admin/tenants', { method: 'POST', json: body });
      setShowCreate(false);
      setCreateForm({ slug: '', company_name: '', admin_email: '', product_id: '', price_cents: '', billing_interval: '', notes: '' });
      // Refresh tenant list
      const d = await api<{ tenants: Tenant[] }>('/admin/tenants');
      setTenants(d.tenants);
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setCreateBusy(false);
    }
  }

  // Bulk push: enqueue an 'update' job for every live tenant. Confirms with
  // the operator first (and uses dry_run to show the exact list) — there's
  // no undo and each tenant briefly restarts.
  async function pushUpdateAll() {
    setPushInfo(null);
    try {
      const dry = await api<{ would_enqueue: Tenant[] }>(
        '/admin/tenants/update-all',
        { method: 'POST', json: { dry_run: true } },
      );
      const n = dry.would_enqueue.length;
      if (n === 0) {
        setPushInfo('No eligible tenants to update.');
        return;
      }
      const ok = window.confirm(
        `Push the current customer-app template to ${n} tenant${n === 1 ? '' : 's'}?\n\n` +
          dry.would_enqueue.map((t) => `  • ${t.slug} (was ${t.app_version ?? '—'})`).join('\n') +
          `\n\nEach tenant gets ~5–10s of API downtime during pm2 restart. ` +
          `Worker processes them one at a time.`,
      );
      if (!ok) return;
      setPushBusy(true);
      const resp = await api<{ enqueued: number }>(
        '/admin/tenants/update-all',
        { method: 'POST', json: { reason: 'dashboard_bulk_push' } },
      );
      setPushInfo(`Enqueued ${resp.enqueued} update jobs. Watch the worker logs for progress.`);
    } catch (e) {
      setPushInfo(`Failed: ${(e as Error).message}`);
    } finally {
      setPushBusy(false);
    }
  }

  // Active = anything currently earning revenue (or about to). Failed/cancelled
  // excluded from the headline number; they show up in the table below.
  const activeCount = stats
    ? (stats.tenants['active'] ?? 0) +
      (stats.tenants['trialing'] ?? 0) +
      (stats.tenants['past_due'] ?? 0)
    : null;

  return (
    <div>
      <h1>Dashboard</h1>
      <p className="subtitle">Tenants currently provisioned on this platform.</p>
      {error && <div className="banner err">{error}</div>}
      {pushInfo && <div className="banner ok">{pushInfo}</div>}

      {stats && (
        <div
          className="kpi-strip"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div className="card" style={{ padding: 14 }}>
            <div className="muted small">Active tenants</div>
            <div style={{ fontSize: 28, fontWeight: 600, marginTop: 4 }}>{activeCount}</div>
            <div className="muted small" style={{ marginTop: 6 }}>
              {Object.entries(stats.tenants)
                .filter(([s]) => ['active', 'trialing', 'past_due'].includes(s))
                .map(([s, n]) => `${s}: ${n}`)
                .join(' · ') || '—'}
            </div>
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div className="muted small">MRR</div>
            <div style={{ fontSize: 28, fontWeight: 600, marginTop: 4 }}>
              {formatMoney(stats.mrr_cents, stats.mrr_currency)}
            </div>
            <div className="muted small" style={{ marginTop: 6 }}>
              annual plans normalized ÷ 12
            </div>
          </div>

          {/* MED-4 from 3rd review: guard jobs_24h / events_24h the same
              way `backups` already is. During a rolling admin-CP restart
              the stats payload can briefly arrive partial; without the
              guard `Object.values(undefined).reduce` blanks the whole
              dashboard. */}
          {stats.jobs_24h && (
            <Link to="/audit?action=tenant&target_type=tenant" style={{ textDecoration: 'none' }}>
              <div className="card" style={{ padding: 14, cursor: 'pointer' }}>
                <div className="muted small">Jobs (last 24h)</div>
                <div style={{ fontSize: 28, fontWeight: 600, marginTop: 4 }}>
                  {Object.values(stats.jobs_24h).reduce((a, b) => a + b, 0)}
                </div>
                <div className="muted small" style={{ marginTop: 6 }}>
                  {(stats.jobs_24h['failed'] ?? 0) > 0 ? (
                    <span style={{ color: 'var(--err)' }}>
                      {stats.jobs_24h['failed']} failed
                    </span>
                  ) : (
                    'no failures'
                  )}
                  {(stats.queue_depth ?? 0) > 0 && ` · ${stats.queue_depth} queued`}
                </div>
              </div>
            </Link>
          )}

          {stats.events_24h && (
            <Link to="/stripe-events" style={{ textDecoration: 'none' }}>
              <div className="card" style={{ padding: 14, cursor: 'pointer' }}>
                <div className="muted small">Stripe events (24h)</div>
                <div style={{ fontSize: 28, fontWeight: 600, marginTop: 4 }}>
                  {stats.events_24h.total}
                </div>
                <div className="muted small" style={{ marginTop: 6 }}>
                  {stats.events_24h.errored > 0 ? (
                    <span style={{ color: 'var(--err)' }}>
                      {stats.events_24h.errored} errored
                    </span>
                  ) : stats.events_24h.unprocessed > 0 ? (
                    <span style={{ color: 'var(--warn)' }}>
                      {stats.events_24h.unprocessed} unprocessed
                    </span>
                  ) : (
                    'all clear'
                  )}
                </div>
              </div>
            </Link>
          )}

          {stats.backups && (() => {
            const b = stats.backups;
            const issues = b.stale + b.failed_last_attempt + b.never_backed_up;
            const total = issues + b.healthy;
            return (
              <div className="card" style={{ padding: 14 }}>
                <div className="muted small">Backups (live tenants)</div>
                <div style={{ fontSize: 28, fontWeight: 600, marginTop: 4 }}>
                  {b.healthy}<span className="muted" style={{ fontSize: 18 }}> / {total}</span>
                </div>
                <div className="muted small" style={{ marginTop: 6 }}>
                  {issues === 0 ? (
                    'all fresh'
                  ) : (
                    <span style={{ color: b.failed_last_attempt > 0 ? 'var(--err)' : 'var(--warn)' }}>
                      {b.failed_last_attempt > 0 && `${b.failed_last_attempt} failed`}
                      {b.failed_last_attempt > 0 && (b.stale + b.never_backed_up) > 0 && ' · '}
                      {b.stale > 0 && `${b.stale} stale`}
                      {b.stale > 0 && b.never_backed_up > 0 && ' · '}
                      {b.never_backed_up > 0 && `${b.never_backed_up} never`}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Actionable ops-health panel — only renders when there's something
          to do. Three issue types: failed-last-attempt (most urgent),
          never-backed-up, stale. Each row is a deep-link to the tenant's
          detail page where the operator can hit "Backup now". */}
      {stats?.backups && stats.backups.tenants_with_issues.length > 0 && (
        <div className="card" style={{ borderLeft: '3px solid var(--warn)', marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>
            Backups need attention
            <span className="muted small" style={{ marginLeft: 8, fontWeight: 'normal' }}>
              {stats.backups.tenants_with_issues.length} tenant
              {stats.backups.tenants_with_issues.length === 1 ? '' : 's'}
            </span>
          </h3>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '4px 0' }}>Tenant</th>
                <th>Issue</th>
                <th>Last successful</th>
                <th>Last attempt</th>
              </tr>
            </thead>
            <tbody>
              {stats.backups.tenants_with_issues.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 0' }}>
                    <Link to={`/tenants/${t.id}`}>{t.slug}</Link>
                  </td>
                  <td>
                    <span className={`pill ${t.issue === 'failed_last_attempt' ? 'err' : 'warn'}`}>
                      {t.issue === 'failed_last_attempt' ? 'last attempt failed'
                       : t.issue === 'never_backed_up'    ? 'never backed up'
                       :                                    'stale (>36h)'}
                    </span>
                  </td>
                  <td className="muted small">
                    {t.last_success_at
                      ? new Date(t.last_success_at).toLocaleString()
                      : '—'}
                  </td>
                  <td className="muted small">{t.last_status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="button-row" style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <button onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Cancel' : '+ Create Tenant'}
        </button>
        {tenants && tenants.length > 0 && (
          <button className="secondary" disabled={pushBusy} onClick={pushUpdateAll}
            title="Re-rsync the customer-app template into every live tenant + pm2 restart. Serial.">
            {pushBusy ? 'Enqueuing…' : 'Push update to all tenants'}
          </button>
        )}
      </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Create Tenant</h3>
          {createError && <div className="banner err" style={{ marginBottom: 12 }}>{createError}</div>}
          <form onSubmit={submitCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label>
                <span className="small muted">Company name</span>
                <input required value={createForm.company_name}
                  onChange={(e) => updateCreate('company_name', e.target.value)}
                  placeholder="Acme Distributors" />
              </label>
              <label>
                <span className="small muted">Subdomain (slug)</span>
                <input required value={createForm.slug}
                  onChange={(e) => updateCreate('slug', e.target.value)}
                  pattern="^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$"
                  placeholder="acme-distributors" />
                <span className="small muted">{createForm.slug && `${createForm.slug}.dealerbosses.com`}</span>
              </label>
              <label>
                <span className="small muted">Admin email</span>
                <input required type="email" value={createForm.admin_email}
                  onChange={(e) => updateCreate('admin_email', e.target.value)}
                  placeholder="admin@example.com" />
              </label>
              <label>
                <span className="small muted">Product / Plan</span>
                <select required value={createForm.product_id}
                  onChange={(e) => updateCreate('product_id', e.target.value)}>
                  <option value="">Select a plan…</option>
                  {products?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {(p.price_cents / 100).toFixed(2)} {p.currency.toUpperCase()}/{p.billing_interval}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="small muted">Price override (cents, optional)</span>
                <input type="number" min="0" value={createForm.price_cents}
                  onChange={(e) => updateCreate('price_cents', e.target.value)}
                  placeholder="Leave blank for plan default" />
              </label>
              <label>
                <span className="small muted">Billing interval override (optional)</span>
                <select value={createForm.billing_interval}
                  onChange={(e) => updateCreate('billing_interval', e.target.value)}>
                  <option value="">Plan default</option>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                <span className="small muted">Notes (optional)</span>
                <input value={createForm.notes}
                  onChange={(e) => updateCreate('notes', e.target.value)}
                  placeholder="Internal tenant, comp'd, etc." />
              </label>
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="submit" disabled={createBusy}>
                {createBusy ? 'Creating…' : 'Create & Provision'}
              </button>
            </div>
          </form>
        </div>
      )}
      {!tenants ? (
        <div className="muted">Loading…</div>
      ) : tenants.length === 0 ? (
        <div className="card muted">No tenants yet. Drive a signup at <a href="https://dealerbosses.com">dealerbosses.com</a>.</div>
      ) : (
        (() => {
          // Filtering layer. Status filter first (cheap), then search.
          const allowedStatuses =
            STATUS_CHIPS.find((c) => c.key === statusFilter)?.statuses ?? [];
          const q = search.trim().toLowerCase();
          const filtered = tenants.filter((t) => {
            if (statusFilter !== 'all' && !allowedStatuses.includes(t.status)) return false;
            if (q) {
              const hay = `${t.slug} ${t.company_name} ${t.admin_email}`.toLowerCase();
              if (!hay.includes(q)) return false;
            }
            return true;
          });

          return (
            <>
              <div className="card" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search slug, company, or admin email…"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {STATUS_CHIPS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className={statusFilter === c.key ? '' : 'secondary'}
                        onClick={() => setStatusFilter(c.key)}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <span className="muted small" style={{ marginLeft: 'auto' }}>
                    {filtered.length === tenants.length
                      ? `${tenants.length} total`
                      : `${filtered.length} of ${tenants.length}`}
                  </span>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="card muted">No tenants match the current filter.</div>
              ) : (
                <div className="card">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '8px 12px' }}>Slug</th>
                        <th style={{ padding: '8px 12px' }}>Company</th>
                        <th style={{ padding: '8px 12px' }}>Admin</th>
                        <th style={{ padding: '8px 12px' }}>Status</th>
                        <th style={{ padding: '8px 12px' }}>Version</th>
                        <th style={{ padding: '8px 12px' }}>Activated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((t) => {
                        const cls =
                          t.status === 'active' ? 'ok' :
                          t.status === 'failed' || t.status === 'cancelled' ? 'err' :
                          t.status === 'provisioning' || t.status === 'pending' ? 'warn' : 'muted';
                        return (
                          <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <Link to={`/tenants/${t.id}`}>{t.slug}</Link>
                            </td>
                            <td style={{ padding: '8px 12px' }}>{t.company_name}</td>
                            <td style={{ padding: '8px 12px' }} className="muted">{t.admin_email}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <span className={`pill ${cls}`}>{t.status}</span>
                            </td>
                            <td style={{ padding: '8px 12px' }} className="code">{t.app_version ?? '—'}</td>
                            <td style={{ padding: '8px 12px' }} className="muted small">
                              {t.activated_at ? new Date(t.activated_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()
      )}
    </div>
  );
}
